<template>
    <div class="tickets-card card mx-1" style="min-height: 200px;">
        <div class="card-header d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center">
            <h3 class="mb-0 mb-sm-0">LockStep Tickets</h3>
            <div class="d-flex flex-wrap gap-2 mt-2 mt-sm-0">
                <button class="btn btn-secondary" type="button"
                    data-bs-toggle="modal" data-bs-target="#support-modal" aria-expanded="false">
                    <img class="icon" src="/img/icons/avid-icons/plus-circle.svg" alt="">Submit Ticket
                </button>
                <!-- Filter Dropdown -->
                <div class="dropdown">
                    <button class="btn btn-secondary dropdown-toggle dropdown-toggle-no-btn" type="button" id="statusFilterDropdown"
                        data-bs-toggle="dropdown" aria-expanded="false">
                        <img class="icon" src="/img/icons/avid-icons/filter-funnel-01.svg" alt="">Status
                    </button>
                    <ul class="dropdown-menu dropdown-menu-primary" aria-labelledby="statusFilterDropdown">
                        <li><a class="dropdown-item" :class="{ active: selectedStatus === 'all' }" href="#"
                                @click.prevent="setFilter('all')">All Tickets</a></li>
                        <li>
                            <hr class="dropdown-divider">
                        </li>
                        <li><a class="dropdown-item" :class="{ active: selectedStatus === 'submitted' }" href="#"
                                @click.prevent="setFilter('submitted')">Submitted</a></li>
                        <li><a class="dropdown-item" :class="{ active: selectedStatus === 'progress' }" href="#"
                                @click.prevent="setFilter('progress')">In Progress</a></li>
                        <li><a class="dropdown-item" :class="{ active: selectedStatus === 'completed' }" href="#"
                                @click.prevent="setFilter('completed')">Completed</a></li>
                    </ul>
                </div>
            </div>
        </div>
        <div class="card-body">
            <div class="tickets">
                <div class="row g-3">
                    <!-- Loop through only the statuses that have tickets -->
                    <div v-for="(ticketList, status) in filteredTickets" :key="status" :class="columnClass"
                        v-if="Object.keys(filteredTickets).length > 0">
                        <div class="h-100">
                            <h6 class="fw-bold mb-3" :class="{
                                'submitted-status': status === 'submitted',
                                'progress-status': status === 'progress',
                                'completed-status': status === 'completed'
                            }">
                                {{ statusLabel(status) }}
                            </h6>

                            <div v-for="ticket in ticketList" :key="ticket.id" class="mb-3 pb-3 border-bottom">
                                <span class=" mb-1 d-flex align-items-center gap-2">
                                    <div class="ticket-icon">
                                        <img src="/img/icons/avid-icons/submit-circle.svg" v-if="status === 'submitted'" class="submitted-status" alt="">
                                        <img src="/img/icons/avid-icons/clock.svg" v-if="status === 'progress'" class="progress-status" alt="">
                                        <img src="/img/icons/avid-icons/check-circle.svg" v-if="status === 'completed'" class="completed-status" alt="">                                        
                                    </div>
                                    <a class="fw-semibold lh-1" href="#" @click="viewTicket(ticket.zendesk_id)">{{ticket.subject }}</a>
                                </span>
                                <p class="small mb-1 ticket-description">{{ ticket.description }}</p>
                                <small class="text-muted d-flex align-items-center gap-1">
                                    Updated: {{ datetime(ticket.updated_at) }}
                                </small>
                            </div>
                        </div>
                    </div>
                    <div v-else class="text-center py-4">
                        No tickets found
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent, computed } from 'vue';
import Button from '../components/Button.vue';
import { useLockstepStore } from '../../stores/lockstep';

export default defineComponent({
    name: 'Tickets',
    components: {
        Button
    },
    props: {

    },
    data() {
        return {
            lockstepStore: useLockstepStore(),
            channel: null,
            selectedStatus: localStorage.getItem('lockstep_ticket_filter') || 'all',
            // Static mock tickets for UI styling
            mockTickets: {
                submitted: [
                    {
                        id: 1,
                        zendesk_id: '12345',
                        subject: 'Issue with data import from Salesforce',
                        description: 'We are experiencing difficulties importing contact data from our Salesforce integration. The sync keeps failing after 50% completion.',
                        created_at: '2025-10-14T10:30:00Z',
                        updated_at: '2025-10-14T10:30:00Z'
                    },
                    {
                        id: 2,
                        zendesk_id: '12346',
                        subject: 'Dashboard loading slowly',
                        description: 'The main dashboard takes over 30 seconds to load. This started happening after the latest update.',
                        created_at: '2025-10-14T14:15:00Z',
                        updated_at: '2025-10-15T08:45:00Z'
                    }
                ],
                progress: [
                    {
                        id: 3,
                        zendesk_id: '12340',
                        subject: 'Custom field mapping request',
                        description: 'Need to map additional custom fields from HubSpot to our customer profiles. Specifically looking to sync company size and industry fields.',
                        created_at: '2025-10-13T09:00:00Z',
                        updated_at: '2025-10-15T11:30:00Z'
                    },
                    {
                        id: 4,
                        zendesk_id: '12338',
                        subject: 'API rate limit errors',
                        description: 'Getting 429 errors when trying to sync more than 1000 records. Need guidance on batch processing.',
                        created_at: '2025-10-12T16:45:00Z',
                        updated_at: '2025-10-14T16:20:00Z'
                    },
                    {
                        id: 5,
                        zendesk_id: '12337',
                        subject: 'User permissions not updating',
                        description: 'Changed user roles but permissions are not reflecting in the application. Users still see old access levels.',
                        created_at: '2025-10-12T11:20:00Z',
                        updated_at: '2025-10-14T10:10:00Z'
                    }
                ],
                completed: [
                    {
                        id: 6,
                        zendesk_id: '12335',
                        subject: 'Export functionality not working',
                        description: 'Unable to export reports to CSV format. Getting a 500 error when clicking the export button.',
                        created_at: '2025-10-11T08:30:00Z',
                        updated_at: '2025-10-13T14:25:00Z'
                    },
                    {
                        id: 7,
                        zendesk_id: '12330',
                        subject: 'Email notifications not received',
                        description: 'Team members are not receiving email notifications for new tasks assigned to them.',
                        created_at: '2025-10-10T13:00:00Z',
                        updated_at: '2025-10-12T09:30:00Z'
                    }
                ]
            }
        };
    },
    computed: {
        tickets() {
            return this.lockstepStore.getTickets;
            // return this.mockTickets; // Using mock data for UI styling - switch back to this.lockstepStore.getTickets when ready
        },
        filteredTickets() {
            if (this.selectedStatus === 'all') {
                return this.tickets;
            }

            // Filter to show only the selected status
            const filtered = {};
            if (this.tickets[this.selectedStatus]) {
                filtered[this.selectedStatus] = this.tickets[this.selectedStatus];
            }
            return filtered;
        },
        selectedStatusLabel() {
            switch (this.selectedStatus) {
                case 'all':
                    return 'All Tickets';
                case 'submitted':
                    return 'Submitted';
                case 'progress':
                    return 'In Progress';
                case 'completed':
                    return 'Completed';
                default:
                    return 'Filter';
            }
        },
        columnClass() {
            const count = Object.keys(this.filteredTickets).length;
            if (count === 3) return "col-lg-4 col-md-6 col-12";
            if (count === 2) return "col-lg-6 col-md-6 col-12";
            return "col-12";
        }
    },
    methods: {
        setFilter(status) {
            this.selectedStatus = status;
            localStorage.setItem('lockstep_ticket_filter', status);
        },
        statusLabel(status) {
            switch (status) {
                case "submitted":
                    return "Submitted";
                case "progress":
                    return "In Progress";
                case "completed":
                    return "Completed";
            }
        },
        viewTicket(ticketId) {
            window.open(`https://help.avidai.com/hc/requests/${ticketId}`, '_blank');
        }
    }
});
</script>

<style scoped>
.tickets {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
</style>